/**
 * Pure mapping from AHP `ToolCallState` to {@link ToolRenderer} props.
 *
 * ToolRenderer reads only `delta.content`: `undefined` → pending shimmer, text →
 * output, `<error>…</error>` → failure (the renderer strips the tags).
 */

import type {ToolCallState, ToolCallResult, ToolResultContent} from '@microsoft/agent-host-protocol'
import type {ToolName, ToolArgs, Delta, ToolExecutionState} from '../types/session'
import type {UserMessageImage} from '../types/user-message-image'
import {stringOrMarkdownToText} from '@github-ui/ahp-relay/ahp-session-selectors'
import {inlineToolInput} from '@github-ui/ahp-relay/tool-input'
import {STREAMED_TOOL_OUTPUT_META_KEY} from '@github-ui/ahp-relay/ahp-chat-store'
import {TOOL_NAMES} from '../constants/tool-names'

const ERROR_OPEN_TAG = ['<', 'error', '>'].join('')
const ERROR_CLOSE_TAG = ['</', 'error', '>'].join('')

// AHP enums are const — compare wire literals (isolatedModules).
const STATUS_RUNNING = 'running'
const STATUS_COMPLETED = 'completed'
const STATUS_PENDING_CONFIRMATION = 'pending-confirmation'
const STATUS_PENDING_RESULT_CONFIRMATION = 'pending-result-confirmation'
const STATUS_CANCELLED = 'cancelled'
const STATUS_AUTH_REQUIRED = 'auth-required'

const STATUS_STREAMING = 'streaming'

const CONTENT_TYPE_TEXT = 'text'
const CONTENT_TYPE_TERMINAL = 'terminal'
const CONTENT_TYPE_EMBEDDED_RESOURCE = 'embeddedResource'

/**
 * Image formats a browser renders inline from a `data:` URL. An allowlist rather
 * than an `image/` prefix test, so formats no browser decodes (e.g. `image/tiff`)
 * do not render as a broken thumbnail.
 */
const INLINE_RENDERABLE_IMAGE_MIMES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp'])

/** A tool state that carries a settled execution result. */
type ToolCallStateWithResult = ToolCallState & ToolCallResult

function hasResult(toolCall: ToolCallState): toolCall is ToolCallStateWithResult {
  return toolCall.status === STATUS_COMPLETED || toolCall.status === STATUS_PENDING_RESULT_CONFIRMATION
}

/**
 * The renderable text a single result block contributes, or `undefined` when
 * the block carries none.
 *
 * Shell tools project their output into a `terminal` block rather than a `text`
 * one: the command's output lives on `result.preview`, and the host omits the
 * sibling text block whenever a terminal block already carries text, so reading
 * `text` alone drops shell output entirely.
 */
function blockText(block: ToolResultContent): string | undefined {
  if (block.type === CONTENT_TYPE_TEXT) return block.text
  if (block.type === CONTENT_TYPE_TERMINAL) return block.result?.preview
  return undefined
}

/**
 * Concatenate the renderable text of a tool result's content, in block order.
 *
 * A terminal block's preview is skipped when an earlier text block already
 * carried exactly that text, so a producer that emits both surfaces the output
 * once.
 */
export function extractText(content: ToolResultContent[] | undefined): string {
  if (!content) return ''

  const segments: string[] = []
  const textBlockValues = new Set<string>()
  for (const block of content) {
    const text = blockText(block)
    if (!text) continue
    if (block.type === CONTENT_TYPE_TERMINAL && textBlockValues.has(text)) continue
    if (block.type === CONTENT_TYPE_TEXT) textBlockValues.add(text)
    segments.push(text)
  }
  return segments.join('')
}

/**
 * Extract inline image previews from a tool result's content. The block carries
 * no label of its own, so `name` comes from the tool input.
 */
export function extractToolResultImages(
  content: ToolResultContent[] | undefined,
  name = 'Tool result',
  alt?: string,
): UserMessageImage[] {
  if (!content) return []

  const images: UserMessageImage[] = []
  for (const block of content) {
    const image = inlineImageBlock(block)
    if (image) images.push({name, ...(alt !== undefined ? {alt} : {}), src: `data:${image.mime};base64,${image.data}`})
  }
  return images
}

export function hasToolResultImages(content: ToolResultContent[] | undefined): boolean {
  return Boolean(content?.some(block => inlineImageBlock(block)))
}

function inlineImageBlock(block: ToolResultContent): {data: string; mime: string} | undefined {
  if (block.type !== CONTENT_TYPE_EMBEDDED_RESOURCE) return undefined
  const {data, contentType} = block as {data?: unknown; contentType?: unknown}
  if (typeof data !== 'string' || !data) return undefined
  if (typeof contentType !== 'string') return undefined
  const mime = normalizeMimeType(contentType)
  return INLINE_RENDERABLE_IMAGE_MIMES.has(mime) ? {data, mime} : undefined
}

/** Reduce a content type to its bare mime, dropping any `;` parameters. */
function normalizeMimeType(contentType: string): string {
  const [mime = ''] = contentType.split(';')
  return mime.trim().toLowerCase()
}

/**
 * AHP delivers `toolCall.toolInput` as the tool's raw *primary* argument (e.g.
 * the bash command string, or the file path for `create`), NOT a JSON object.
 * The tool renderers, however, read a structured `args` object keyed by field
 * name. This maps each single-scalar-input tool to the arg key its renderer
 * reads, so a raw `toolInput` is coerced into `{[key]: rawInput}` instead of
 * being dropped. Multi-argument tools are absent here and rely on the JSON
 * passthrough in {@link parseArgs}.
 */
const PRIMARY_TOOL_ARG_KEY: Record<string, string> = {
  // Shell/terminal: the command that runs.
  [TOOL_NAMES.BASH]: 'command',
  [TOOL_NAMES.LOCAL_SHELL]: 'command',
  [TOOL_NAMES.POWERSHELL]: 'command',
  [TOOL_NAMES.ASYNC_BASH]: 'command',
  // Session-scoped shell ops key off the session id.
  [TOOL_NAMES.READ_BASH]: 'shellId',
  [TOOL_NAMES.STOP_BASH]: 'shellId',
  [TOOL_NAMES.READ_POWERSHELL]: 'shellId',
  [TOOL_NAMES.STOP_POWERSHELL]: 'shellId',
  [TOOL_NAMES.READ_ASYNC_BASH]: 'shellId',
  [TOOL_NAMES.STOP_ASYNC_BASH]: 'shellId',
  // File tools: the target path.
  [TOOL_NAMES.CREATE]: 'path',
  [TOOL_NAMES.VIEW]: 'path',
  [TOOL_NAMES.STR_REPLACE_EDITOR]: 'path',
  [TOOL_NAMES.STR_REPLACE]: 'path',
  [TOOL_NAMES.EDIT]: 'path',
  [TOOL_NAMES.INSERT]: 'path',
  [TOOL_NAMES.UNDO_EDIT]: 'path',
  [TOOL_NAMES.APPLY_PATCH]: 'content',
  // Search tools.
  [TOOL_NAMES.GREP]: 'pattern',
  [TOOL_NAMES.RG]: 'pattern',
  [TOOL_NAMES.GLOB]: 'pattern',
  [TOOL_NAMES.SEARCH_CODE_SUBAGENT]: 'query',
  [TOOL_NAMES.LEXICAL_CODE_SEARCH]: 'query',
  [TOOL_NAMES.SEMANTIC_CODE_SEARCH]: 'query',
  [TOOL_NAMES.GITHUB_MCP_SERVER_SEARCH_CODE]: 'query',
  [TOOL_NAMES.WEB_SEARCH]: 'query',
  [TOOL_NAMES.GITHUB_MCP_SERVER_WEB_SEARCH]: 'query',
  [TOOL_NAMES.GITHUB_MCP_SERVER_BING_SEARCH]: 'query',
  [TOOL_NAMES.WEB_FETCH]: 'url',
  // Communication.
  [TOOL_NAMES.THINK]: 'thought',
  [TOOL_NAMES.SKILL]: 'skill',
  [TOOL_NAMES.REPLY_TO_COMMENT]: 'reply',
}

/**
 * Coerce an AHP `toolInput` into the structured `args` object the renderers
 * expect.
 *
 * `toolInput` may be either a JSON object (structured, multi-argument tools) or
 * the tool's raw primary scalar (e.g. a bash command or file path). A JSON
 * object is returned as-is; a raw scalar is wrapped under the tool's
 * {@link PRIMARY_TOOL_ARG_KEY primary arg key}. When the input is neither a
 * JSON object nor a known single-scalar tool, an empty object is returned.
 */
function parseArgs(rawInput: string | undefined, toolName: string): ToolArgs {
  if (!rawInput) return {}

  let parsed: unknown
  try {
    parsed = JSON.parse(rawInput)
  } catch {
    parsed = undefined
  }

  // Structured input: a JSON object is already in renderer-args shape.
  if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return parsed
  }

  // Raw scalar input: wrap it under the tool's primary arg key. Prefer a parsed
  // JSON string (e.g. a quoted `"path"`) over the original when available.
  const key = PRIMARY_TOOL_ARG_KEY[toolName]
  if (key) {
    return {[key]: typeof parsed === 'string' ? parsed : rawInput}
  }

  return {}
}

function pendingConfirmationDescription(toolCall: ToolCallState): string | undefined {
  if (toolCall.status !== STATUS_PENDING_CONFIRMATION && toolCall.status !== STATUS_PENDING_RESULT_CONFIRMATION) {
    return undefined
  }

  const title = 'confirmationTitle' in toolCall ? stringOrMarkdownToText(toolCall.confirmationTitle) : undefined
  return title ?? stringOrMarkdownToText(toolCall.invocationMessage)
}

function addPendingDescription(args: ToolArgs, description: string | undefined): ToolArgs {
  if (!description || typeof args !== 'object' || Array.isArray(args) || 'description' in args) {
    return args
  }

  return {...args, description}
}

/**
 * The raw tool input to render args from.
 *
 * A `streaming` call has no consolidated `toolInput` yet — the host sends that
 * on the `chat/toolCallReady` that leaves `streaming` — so fall back to the
 * `partialInput` the reducer accumulates from `chat/toolCallDelta`. Without it
 * a running tool renders with empty args (no command, no description) until it
 * completes.
 *
 * `partialInput` is only used once it parses as JSON. It accumulates the tool's
 * *parameters*, so a chunk boundary mid-object would otherwise be wrapped as
 * the tool's primary arg and rendered as a truncated command line.
 */
function rawToolInput(toolCall: ToolCallState): string | undefined {
  const toolInput = inlineToolInput('toolInput' in toolCall ? toolCall.toolInput : undefined)
  if (toolInput) return toolInput
  if (toolCall.status !== STATUS_STREAMING || !toolCall.partialInput) return undefined
  return isJson(toolCall.partialInput) ? toolCall.partialInput : undefined
}

function isJson(value: string): boolean {
  try {
    JSON.parse(value)
    return true
  } catch {
    return false
  }
}

/**
 * The body to render for a failed tool call, wrapped in the error tags the
 * renderer keys on.
 *
 * The producer fills in a result's `content` whether or not the call succeeded,
 * so a failed shell command still carries its output — the compiler error, the
 * failing test, the stack trace. That output is the useful part of a failure,
 * so it is preferred over the error message, which is often just a restatement
 * of the exit status.
 *
 * The message is appended when it says something the output does not, and
 * stands alone when there is no output at all.
 */
function failureContent(toolCall: ToolCallStateWithResult): string {
  const output = extractText(toolCall.content)
  const message = toolCall.error?.message

  let body: string
  if (!output) {
    body = message ?? 'Tool execution failed'
  } else if (message && !output.includes(message)) {
    body = `${output}\n${message}`
  } else {
    body = output
  }

  return `${ERROR_OPEN_TAG}${body}${ERROR_CLOSE_TAG}`
}

/**
 * Output streamed into a still-`streaming` tool call by `AhpChatStore`.
 *
 * Server tools stay `streaming` for their whole execution — copilot-host defers
 * the Ready that would promote them to `running` until they complete — and that
 * state has no `content` field, so the store parks the accumulated output on
 * `_meta` instead.
 */
function streamedOutput(toolCall: ToolCallState): string | undefined {
  const output = (toolCall as {_meta?: Record<string, unknown>})._meta?.[STREAMED_TOOL_OUTPUT_META_KEY]
  return typeof output === 'string' && output ? output : undefined
}

/** Labels for a tool result image: an identifier and a purpose-based text alternative. */
function imageLabels(args: ToolArgs, toolName: string | undefined): Pick<UserMessageImage, 'name' | 'alt'> {
  const normalizedToolName = toolName ?? ''
  const readableToolName = normalizedToolName.replaceAll(/[-_]/g, ' ').trim()
  let name: string | undefined

  if (typeof args === 'object' && args !== null && !Array.isArray(args)) {
    const {path, filename} = args as {path?: unknown; filename?: unknown}
    const source =
      typeof path === 'string' && path ? path : typeof filename === 'string' && filename ? filename : undefined
    name = source?.split(/[\\/]/).pop()
  }

  const fallbackName = readableToolName ? `${readableToolName} result` : 'Tool result'
  if (normalizedToolName === 'view') return {name: name ?? fallbackName, alt: 'Preview of the viewed file'}
  if (normalizedToolName.toLowerCase().includes('screenshot')) {
    return {name: name ?? fallbackName, alt: 'Screenshot captured by the browser automation tool'}
  }
  return {name: name ?? fallbackName, alt: readableToolName ? `Output from ${readableToolName}` : 'Tool output'}
}

export function ahpToolCallToToolProps(toolCall: ToolCallState): {
  name: ToolName
  args: ToolArgs
  delta: Delta
  isMcpTool: boolean
  executionState: ToolExecutionState
} {
  const rawInput = rawToolInput(toolCall)
  const args = addPendingDescription(parseArgs(rawInput, toolCall.toolName), pendingConfirmationDescription(toolCall))

  let content: string | undefined
  let images: UserMessageImage[] = []
  const labels = imageLabels(args, toolCall.toolName)
  if (hasResult(toolCall)) {
    content = toolCall.success ? extractText(toolCall.content) : failureContent(toolCall)
    if (toolCall.success) images = extractToolResultImages(toolCall.content, labels.name, labels.alt)
  } else if (toolCall.status === STATUS_RUNNING) {
    content = toolCall.content ? extractText(toolCall.content) : undefined
    images = extractToolResultImages(toolCall.content, labels.name, labels.alt)
  } else if (toolCall.status === STATUS_STREAMING) {
    content = streamedOutput(toolCall)
  } else if (toolCall.status === STATUS_CANCELLED) {
    // Cancelled tools are settled, not pending — undefined content would shimmer
    // forever. (Denied cancellations are diverted to a permission entry upstream.)
    content = stringOrMarkdownToText(toolCall.reasonMessage) ?? ''
  } else if (toolCall.status === STATUS_AUTH_REQUIRED) {
    content = toolCall.auth.description ?? 'Authentication is required to continue.'
  }

  return {
    name: toolCall.toolName,
    args,
    delta: {content, images: images.length > 0 ? images : undefined},
    isMcpTool: toolCall.contributor?.kind === 'mcp',
    executionState:
      toolCall.status === STATUS_COMPLETED
        ? toolCall.success
          ? 'completed'
          : 'failed'
        : toolCall.status === STATUS_CANCELLED
          ? 'failed'
          : 'running',
  }
}
